import { LivancePay, Right } from "@repo/icons";
import { graphql } from "@repo/graphql-types";
import { Link, useRouteContext } from "@tanstack/react-router";
import { format, formatWithZonedDate } from "@repo/lib";
import { endOfMonth, startOfMonth } from "date-fns";
import {
  SectionHeaderButton,
  SectionHeaderContent,
  SectionHeaderDescription,
  SectionHeaderIcon,
  SectionHeaderInfo,
  SectionHeaderRoot,
  SectionHeaderTitle,
} from "@/components/section-header";
import { useGraphQL } from "@/hooks/use-graphql";
import { AsyncDataWrapper } from "@/components/async-data-wrapper";
import { HomeSectionLivancePayCardGroup } from "@/components/home/home-section-livance-pay-card-group";
import { ErrorCard } from "@/components/home/error-card";
import { LivancePayCardGroupSkeleton } from "@/components/home/Skeleton/livance-pay-card-group-skeleton";

export const HomeSectionLivancePayQuery = graphql(`
  query HomeSectionLivancePayQuery(
    $dateStartAction: String!
    $dateEndAction: String!
    $dateStartCount: timestamptz!
    $dateEndCount: timestamptz!
    $codUsuario: Int!
    $codClinica: Int
  ) {
    ...HomeSectionLivancePayCardGroupFragment
    expensumRecipient: ExpensumGetRecipient(
      arg1: { codUsuario: $codUsuario, codClinica: $codClinica }
    ) {
      recipient {
        pagarmeId
      }
    }
  }
`);

export const HomeSectionLivancePay = (): JSX.Element => {
  const { user } = useRouteContext({
    strict: false,
  });
  const now = new Date();
  const dateStartCount = startOfMonth(now).toISOString();
  const dateEndCount = startOfMonth(
    new Date(now.getFullYear(), now.getMonth() + 1, 1),
  ).toISOString();

  const dateStartAction = format(startOfMonth(now), "yyyy-MM-dd");
  const dateEndAction = format(endOfMonth(now), "yyyy-MM-dd");

  const queryResult = useGraphQL(HomeSectionLivancePayQuery, {
    dateStartAction,
    dateEndAction,
    dateStartCount,
    dateEndCount,
    codUsuario: user.codUsuario,
    codClinica: user.codClinica,
  });

  const { data, refetch } = queryResult;
  const periodDetails = `${formatWithZonedDate(dateStartAction, "dd MMMM yyyy")} - ${formatWithZonedDate(dateEndAction, "dd MMMM yyyy")}`;
  const refetchPayQuery = (): void => {
    void refetch();
  };
  const hasRecipient = Boolean(data?.expensumRecipient?.recipient?.pagarmeId);

  return (
    <div className="gap-4 flex flex-col">
      <SectionHeaderRoot>
        <SectionHeaderContent>
          <SectionHeaderIcon className="bg-white">
            <LivancePay size={16} className="fill-primary-400" />
          </SectionHeaderIcon>
          <SectionHeaderInfo>
            <SectionHeaderTitle>Livance Pay</SectionHeaderTitle>
            <SectionHeaderDescription>
              Gestão financeira integrada
            </SectionHeaderDescription>
          </SectionHeaderInfo>
        </SectionHeaderContent>
        {hasRecipient ? (
          <Link to="/settings/payments">
            <SectionHeaderButton>
              Ver todos <Right size={16} />
            </SectionHeaderButton>
          </Link>
        ) : (
          <div />
        )}
      </SectionHeaderRoot>

      <AsyncDataWrapper
        {...queryResult}
        errorFallback={<ErrorCard onClick={refetchPayQuery} />}
        fallback={<LivancePayCardGroupSkeleton />}
      >
        {data ? (
          <HomeSectionLivancePayCardGroup data={data} periodDetails={periodDetails} />
        ) : null}
      </AsyncDataWrapper>
    </div>
  );
};
